import React, { useState, useEffect, useCallback, useContext } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { Route, Routes, Navigate, useMatch, BrowserRouter as Router } from "react-router-dom"
import ClientData, { type EditingState } from "@/common/client_data"
import LoadingAnimation from "@/common/components/loading"
import env from "@/common/env"
import storageLocal from "@/common/storage"
import { metaNameSign, metaPlasticSign, metaCableSign } from "@/common/util"
import FrostyPattern from "@/images/signomatic/materials/frosty_pattern.png"
import type Sign from "@/signomatic/app/sign"
import Background from "@/signomatic/components/background"
import FileImport from "@/signomatic/components/file_import/index"
import FontUpload from "@/signomatic/components/font_upload/index"
import ToolboxStrip from "@/signomatic/components/toolbox_strip"
import { SignContext } from "@/signomatic/contexts"
import { type State } from "@/signomatic/store/reducers"
import { type Fontdata } from "@/signomatic/util/fonts"

interface RouteProps {
  type: string
  sign?: Sign
  title?: string
}

const RouteUpdate = ({ type, sign, title }: RouteProps) => {
  const setSignType = useCallback(
    (type: string) => {
      sign?.setSignType(type)
    },
    [sign]
  )

  const setMetaSignType = useCallback(
    (metaProduct: string) => {
      sign?.setMetaSignType(metaProduct)
    },
    [sign]
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const metaProducts = ClientData.metaProducts || []
  const admin = ClientData.admin || false

  if (!admin && ["tactile_sign", "cable_tag"].includes(type)) {
    type = "root"
  }

  useEffect(() => {
    if (metaProducts.includes(type)) {
      setMetaSignType(type)
    } else if (type !== "root") {
      setSignType(type)
    }
  }, [type, setSignType, metaProducts, setMetaSignType])

  if (type === "root") {
    document.body.classList.add("rootloaded")
    return <></>
  }

  if (title) {
    document.title = title
  }

  document.body.classList.remove("rootloaded")
  return <></>
}

interface RoutesProps {
  sign: Sign
  signs: SignType[]
}

const SignomaticRoutes: React.FC<RoutesProps> = ({ sign, signs }) => {
  const { t } = useTranslation()
  const [loadToolbox, setLoadToolbox] = useState(false)

  const triggerLoadToolbox = useCallback(() => {
    if (loadToolbox) {
      return
    }

    setTimeout(() => {
      setLoadToolbox(true)
    }, 10)
  }, [loadToolbox, setLoadToolbox])

  useEffect(() => {
    sign.onLoad.subscribe(triggerLoadToolbox)

    if (sign.isLoaded) {
      triggerLoadToolbox()
    }
  }, [sign, triggerLoadToolbox])

  const rootMatch = useMatch({ path: ClientData.rootPath || "" })
  const startMatch = useMatch({ path: ClientData.startPath || "" })

  useEffect(() => {
    if (rootMatch || startMatch) {
      triggerLoadToolbox()
    }
  }, [rootMatch, startMatch, triggerLoadToolbox])

  let pathToSign: string | null = null
  const signTypeFromLocalStorage = storageLocal.getItem("signType")
  let metaProductFromLocalStorage = storageLocal.getItem("metaProduct")

  if (signTypeFromLocalStorage) {
    if (metaPlasticSign(signTypeFromLocalStorage)) {
      metaProductFromLocalStorage = "meta_plastic_sign"
    }

    if (metaNameSign(signTypeFromLocalStorage)) {
      metaProductFromLocalStorage = "meta_name_sign"
    }

    if (metaCableSign(signTypeFromLocalStorage)) {
      metaProductFromLocalStorage = "cable_tag"
    }
  }

  if (metaProductFromLocalStorage && signs.map(s => s.key).includes(metaProductFromLocalStorage)) {
    pathToSign = env.languageSegment + t("route.signomatic") + t(`route.${metaProductFromLocalStorage}`)
  } else if (signTypeFromLocalStorage && signs.map(s => s.key).includes(signTypeFromLocalStorage)) {
    pathToSign = env.languageSegment + t("route.signomatic") + t(`route.${signTypeFromLocalStorage}`)
  }

  return (
    <>
      <Routes>
        {signs.map(type => (
          <Route
            key={type.key}
            path={type.signomatic_path}
            element={<RouteUpdate type={type.key} title={type.title} sign={sign} />}
          />
        ))}
        <Route
          key="root"
          path={ClientData.rootPath || ""}
          element={pathToSign ? <Navigate to={pathToSign} replace /> : <RouteUpdate type="root" />}
        />
        <Route key="start" path={ClientData.startPath || ""} element={<RouteUpdate type="root" />} />
      </Routes>
      {!ClientData.skipToolbox && loadToolbox ? <ToolboxStrip sign={sign} /> : <div className="toolbox-strip" />}
    </>
  )
}

const SignomaticRouter: React.FC<{ orderId: string; adminId: string; fontdata: Fontdata }> = ({
  orderId,
  adminId,
  fontdata,
}) => {
  const sign = useContext(SignContext)
  const signTypes = useSelector<State, SignType[]>(({ signs }) => signs)
  const editing = useSelector<State, EditingState | null>(state => state.editing)

  if (!sign) {
    return null
  }

  return (
    <Router>
      <React.Fragment>
        <FileImport sign={sign} orderId={orderId} />
        <FontUpload
          sign={sign}
          orderId={editing?.orderId?.toString() || orderId}
          adminId={adminId}
          fontdata={fontdata}
        />
        <div id="signomatic" className="relative min-w-0">
          {sign.signType !== null && (
            <>
              <div className="signomatic-loading absolute inset-0 z-40 flex items-center justify-center bg-gray-200 opacity-30">
                <LoadingAnimation className="!h-40 !w-40" />
              </div>
              <div className="sign-update collapse absolute inset-0 z-40 hidden items-center justify-center bg-gray-200 opacity-30">
                <LoadingAnimation className="!h-40 !w-40" />
              </div>
            </>
          )}
          <svg width="0" height="0" style={{ position: "absolute" }}>
            <defs>
              <symbol id="svg_frosty_pattern">
                <image href={FrostyPattern} x="8" y="8" width="496" height="496" opacity="0.288" />
              </symbol>
            </defs>
          </svg>
          <SignomaticRoutes signs={signTypes} sign={sign} />
          <Background sign={sign} />
        </div>
      </React.Fragment>
    </Router>
  )
}

export default SignomaticRouter
